/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import AsyncSelect from 'react-select/async'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import { _object } from 'utils/interfaces'

import { dealsService, mediaService, productService } from 'services'

import { InputField } from 'components/form-inputs'

import { DeleteIcon } from 'assets/svg'
import { Placeholder } from 'assets/images'
import { toast } from 'react-toastify'
import store from 'store'

const DealDetails = () => {
  const [deal, setDeal] = useState<_object>({})
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedProducts, setSelectedProducts] = useState<_object[]>([])
  const [displayProducts, setDisplayProducts] = useState<_object[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const fetchDealDetails = () => {
    dealsService.getDealDetails(id).then((results: _object) => {
      setDeal(results)
    })
  }

  useEffect(() => {
    fetchDealDetails()
  }, [id])
  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    purchase_qty: Yup.number().required('Purchase quantity is required'),
    reward_qty: Yup.number().required('Reward quantity is required'),
    start_date: Yup.string().required('Start date is required'),
    end_date: Yup.string().required('End date is required'),
    status: Yup.string().required('Status is required'),
    product_ids: Yup.array()
      .min(1, 'At least one product must be selected')
      .of(Yup.string().required())
      .required('Product is required')
  })

  const formik: any = useFormik({
    initialValues: {
      name: deal?.name || '',
      purchase_qty: deal?.purchase_qty || '',
      reward_qty: deal?.reward_qty || '',
      start_date: deal?.start_date || '',
      end_date: deal?.end_date || '',
      status: deal?.status,
      product_ids: []
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)

      dealsService.updateDeal(id, values).then((results: _object) => {
        if (results.error === false) {
          setLoading(false)
          fetchDealDetails()
          location.href = '/app/deals'
        }
      })
    }
  })

  const loadProductOptions = async (inputValue: string) => {
    try {
      const response = await productService.getProducts({
        q: inputValue,
        page: 1,
        per_page: 20,
        sort: 'created_at',
        stock_status: 'stock'
      })

      return response.items.map((item: _object) => ({
        value: item,
        label: item.title
      }))
    } catch (error) {
      console.error('Failed to load products', error)
      return []
    }
  }

  useEffect(() => {
    const dealItems =
      deal?.products?.map((item: _object) => ({
        ...item,
        source: 'deal'
      })) || []

    const selectedItems = selectedProducts.map((item: _object) => ({
      ...item,
      source: 'selected'
    }))

    const merged = [...dealItems, ...selectedItems].filter(
      (item, index, self) => index === self.findIndex((i) => i.id === item.id)
    )

    setDisplayProducts(merged)
  }, [deal, selectedProducts])

  const handleDeleteProduct = (id: string, source: string) => {
    if (source === 'deal') {
      setDeal((prev) => ({
        ...prev,
        products: prev.products?.filter((item: _object) => item.id !== id)
      }))
    } else {
      setSelectedProducts((prev) => prev.filter((item: _object) => item.id !== id))
    }
  }

  useEffect(() => {
    const productIds = displayProducts.map((item) => item.id)
    formik.setFieldValue('product_ids', productIds)
  }, [selectedProducts, displayProducts])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null
    if (file) {
      setSelectedFile(file)
    }
  }
  useEffect(() => {
    if (!selectedFile) return
    setIsUploading(true)

    const uploadImage = async () => {
      const data = new FormData()
      if (id) {
        data.append('entity_id', id)
      }
      data.append('entity_type', 'deal')
      data.append('key', 'deal_image')
      data.append('files', selectedFile)
      const accessToken = store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || '')

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/media`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: accessToken ? accessToken : ''
        },
        body: data
      })

      const result = await response.json()
      if (result.error === false) {
        toast.success(result.message || 'Image uploaded successfully.', {
          autoClose: 3000
        })
      } else if (result.error === true) {
        toast.error(result.message || 'Image not uploaded.', {
          autoClose: 3000
        })
      }
      setIsUploading(false)
    }

    uploadImage()
  }, [selectedFile])

  const deleteSlide = (id: string) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      mediaService.deleteMediaData(id).then((results: _object) => {
        if (results?.error === false) {
          fetchDealDetails()
        }
      })
    }
  }
  return (
    <>
      <div className="mb-6">
        <h1 className="mb-1 text-2xl font-semibold">Edit Deal</h1>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="mb-4">
              <InputField
                label="Deal Name"
                type="text"
                id="name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:outline-none"
                placeholder="Deal Name"
                required={true}
              />
            </div>
            <div className="mb-4">
              <InputField
                label="Purchase Quantity"
                type="number"
                id="purchase_qty"
                name="purchase_qty"
                value={formik.values.purchase_qty}
                onChange={formik.handleChange}
                className="w-full rounded-md border px-3 py-2 text-sm outline-none focus:outline-none"
                placeholder="Purchase Quantity"
                required={true}
                min={1}
              />
            </div>

            <div className="mb-4">
              <InputField
                label="Reward Quantity"
                type="number"
                name="reward_qty"
                value={formik.values.reward_qty}
                onChange={formik.handleChange}
                className="w-full rounded-md border px-3 py-2 text-sm font-medium text-gray-900 outline-none focus:outline-none"
                placeholder="Reward Quantity"
                required={true}
                min={1}
              />
            </div>

            <div className="mb-4">
              <label htmlFor="start_date" className="mb-2 block text-sm font-medium text-gray-900">
                Deal Start Date
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formik.values.start_date ? formik.values.start_date.split('T')[0] : ''}
                onChange={(e) => {
                  const selectedDate = e.target.value // "2025-06-10"
                  const isoDate = new Date(selectedDate).toISOString() // "2025-06-10T00:00:00.000Z"
                  formik.setFieldValue('start_date', isoDate) // update form state
                }}
                className="w-full rounded-md border bg-gray-100 px-3 py-2 text-sm outline-none focus:outline-none"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="dealStartDate"
                className="mb-2 block text-sm font-medium text-gray-900"
              >
                Deal End Date
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formik.values.end_date ? formik.values.end_date.split('T')[0] : ''}
                onChange={(e) => {
                  const selectedDate = e.target.value
                  const isoDate = new Date(selectedDate).toISOString()
                  formik.setFieldValue('end_date', isoDate)
                }}
                className="w-full rounded-md border bg-gray-100 px-3 py-2 text-sm outline-none focus:outline-none"
              />
            </div>

            <div className="mb-5">
              <label className="mb-2 flex text-sm leading-none font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="status"
                    value="active"
                    checked={formik.values.status === 'active'}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  Active
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="status"
                    value="inactive"
                    checked={formik.values.status === 'inactive'}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  Inactive
                </label>
              </div>
            </div>

            <div className="mb-4">
              {deal.image ? (
                <div className="relative inline-block h-[120px] w-[120px]">
                  <img src={deal.image} alt={deal.name} className="rounded-md h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => deleteSlide(deal.image_id)}
                    title="Delete Slide"
                    className="absolute top-1 right-1 rounded-full bg-white p-1 shadow-sm hover:bg-gray-100"
                  >
                    <DeleteIcon className="h-[20px] w-[20px] text-red-500" />
                  </button>
                </div>
              ) : (
                <>
                  <label className="mb-1 flex text-sm leading-none font-medium text-gray-700">
                    Deal Image <span className="text-red-500">*</span>
                  </label>
                  <p className="mb-2 text-[10px] text-[#969494] font-medium">227W × 170H | 5:4</p>
                  <label htmlFor="imageUpload">
                    <div className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-purple-900">
                      <input
                        type="file"
                        id="imageUpload"
                        accept=".png, .jpg, .jpeg"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      {isUploading ? (
                        <svg
                          className="h-8 w-8 animate-spin text-purple-900"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                      ) : (
                        <span className="text-4xl font-bold text-purple-900">+</span>
                      )}
                    </div>
                  </label>
                </>
              )}
            </div>

            <button
              type="submit"
              className={`rounded bg-purple-900 hover:bg-purple-950 font-medium px-4 py-2 text-white text-sm ${loading == true && 'opacity-50 hover:bg-purple-950'}`}
            >
              {location.pathname.endsWith('/add') ? 'Add Deal' : 'Save Changes'}
            </button>
          </div>
          <div className="p-5">
            <h3 className="mb-2 text-xl font-semibold">
              Products list
              <span className="text-red-500">*</span>
            </h3>
            <div className="space-y-4">
              {displayProducts.map((item: _object) => {
                return (
                  <div
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-4"
                    key={item.id}
                  >
                    <div className="mr-3 flex-shrink-0 flex items-center gap-4">
                      <img
                        src={item.image || Placeholder}
                        className="h-10 w-10 rounded-md"
                        alt={item.title}
                      />
                      <div>
                        <h6 className="font-medium text-purple-900">
                          {item?.new_title ? item?.new_title : item.title}
                        </h6>
                        <p className="text-sm text-gray-400">SKU: {item.sku}</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => handleDeleteProduct(item.id, item.source)}>
                      <DeleteIcon />
                    </button>
                  </div>
                )
              })}

              <div>
                {!open && (
                  <div
                    className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-purple-900"
                    onClick={() => {
                      setOpen(true)
                    }}
                  >
                    <span className="text-3xl font-bold text-purple-900">
                      <div>+</div>
                    </span>
                  </div>
                )}

                {open && (
                  <div className="mt-4">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadProductOptions}
                      onChange={(selectedOption: any) => {
                        if (selectedOption) {
                          const exists = displayProducts.some(
                            (prod) => prod.id === selectedOption.value.id
                          )
                          if (!exists) {
                            setSelectedProducts((prev) => [...prev, selectedOption.value])
                          }
                        }
                      }}
                      placeholder="Search and select product"
                      isSearchable
                      isClearable
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

export default DealDetails
